import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Pipette } from 'lucide-react';
import { useEditor } from '../../context/EditorContext';
import { renderPageToCanvas } from '../../lib/renderUtils';
import { getSampler, subscribeSampler, finishSampling, stopSampling, pixelToHex } from '../../lib/colorSampler';

/**
 * Crosshair overlay shown over the current page while a colour picker is sampling. The page (objects, pictures, master
 * objects, background) is rendered once to an offscreen canvas at 1 px per page px; moving the mouse reads the pixel under
 * the cursor and shows it next to the crosshair; clicking picks it; Escape or clicking outside the page cancels.
 * Rendered inside the page element so it covers exactly the page at any zoom.
 */
export const SampleColorOverlay = ({ page, master }) => {
  const { state } = useEditor();
  const [sampler, setSampler] = useState(getSampler());
  const [canvas, setCanvas] = useState(null);
  const [preview, setPreview] = useState(null); // { hex, x, y } in overlay px
  const overlayRef = useRef(null);

  useEffect(() => subscribeSampler(setSampler), []);

  // Render the page once per sampling session
  useEffect(() => {
    if (!sampler.active) { setCanvas(null); setPreview(null); return undefined; }
    let cancelled = false;
    setCanvas(null);
    // 1 px per page px, capped so very large pages (banners) do not allocate a giant canvas
    const scale = Math.min(1, 4096 / Math.max(page?.width || 1, page?.height || 1));
    renderPageToCanvas(page, master, state.textFlow, scale)
      .then((c) => { if (!cancelled) setCanvas(c); })
      .catch(() => { if (!cancelled) setCanvas(null); });
    return () => { cancelled = true; };
    // one render per sampling session (the snapshot the eyedropper reads from): page / master / text flow are taken as
    // they are when sampling starts — re-rendering on every document change would discard the pixels mid-sample
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampler.active]);

  // Escape cancels; a mousedown anywhere outside the page cancels too
  useEffect(() => {
    if (!sampler.active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); stopSampling(); } };
    const onDown = (e) => { if (overlayRef.current && !overlayRef.current.contains(e.target)) stopSampling(); };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onDown, true);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('mousedown', onDown, true); };
  }, [sampler.active]);

  if (!sampler.active) return null;

  const bg = page?.background?.type === 'solid' ? page.background.color : (page?.background?.color || '#ffffff');

  const sampleAt = (e) => {
    if (!canvas || !overlayRef.current) return null;
    const rect = overlayRef.current.getBoundingClientRect();
    const px = Math.floor(((e.clientX - rect.left) / rect.width) * canvas.width);
    const py = Math.floor(((e.clientY - rect.top) / rect.height) * canvas.height);
    if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) return null;
    try {
      const [r, g, b, a] = canvas.getContext('2d').getImageData(px, py, 1, 1).data;
      return { hex: pixelToHex(r, g, b, a, bg), x: e.clientX - rect.left, y: e.clientY - rect.top };
    } catch { return null; }
  };

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0"
      style={{ zIndex: 5000, cursor: canvas ? 'crosshair' : 'progress' }}
      onMouseMove={(e) => setPreview(sampleAt(e))}
      onMouseLeave={() => setPreview(null)}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); const s = sampleAt(e); if (s) finishSampling(s.hex); }}
      onContextMenu={(e) => { e.preventDefault(); stopSampling(); }}
      role="application"
      aria-label={`Sample ${sampler.label}: click a colour on the page, Escape to cancel`}
      data-testid="sample-color-overlay"
      data-keep-editing
      data-ready={canvas ? '1' : '0'}
    >
      {!canvas && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2 flex items-center gap-2 rounded-md bg-popover/95 backdrop-blur px-3 py-1.5 text-xs text-popover-foreground border border-border shadow-md" data-testid="sample-color-preparing"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing the page…</div>
      )}
      {canvas && !preview && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2 flex items-center gap-2 rounded-md bg-popover/95 backdrop-blur px-3 py-1.5 text-xs text-popover-foreground border border-border shadow-md pointer-events-none" data-testid="sample-color-hint"><Pipette className="h-3.5 w-3.5" /> Click any colour on the page to sample it · Esc to cancel</div>
      )}
      {preview && (
        <div className="absolute flex items-center gap-2 rounded-md bg-popover/95 backdrop-blur px-2 py-1 text-xs font-mono text-popover-foreground border border-border shadow-md pointer-events-none" style={{ left: preview.x + 16, top: preview.y + 16 }} data-testid="sample-color-preview" data-hex={preview.hex}>
          <span className="h-5 w-5 rounded-sm border border-black/10 dark:border-white/20" style={{ background: preview.hex }} />
          {preview.hex.toUpperCase()}
        </div>
      )}
    </div>
  );
};
